import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import RedesignedHero from "@/components/redesigned/hero";
import { SidebarProvider } from "@/components/ui/sidebar";
import { toast } from "@/hooks/ui/use-toast";

const Index = () => {
  const textBannerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Handle auth from URL hash (recovery links and errors)
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.substring(1));
    const error = params.get('error');
    const errorCode = params.get('error_code');
    const errorDescription = params.get('error_description');
    const isRecoveryType = params.get('type') === 'recovery';

    // Forward valid recovery links to sign-in (preserve hash for processing)
    if (isRecoveryType) {
      navigate('/sign-in' + window.location.hash, { replace: true });
      return;
    }

    // Handle auth errors (e.g., expired password reset links)
    if (error) {
      const message = errorDescription
        ? decodeURIComponent(errorDescription.replace(/\+/g, ' '))
        : 'An authentication error occurred';

      toast.error("Authentication Error", {
        description: errorCode === 'otp_expired'
          ? "The email link has expired. Please request a new one."
          : message,
      });

      // Clean up the URL hash - stay on landing page (don't navigate away)
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [navigate]);

  useEffect(() => {
    const handleScroll = () => {
      if (!textBannerRef.current || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Calculate scroll progress when container is in view
      // Start animation sooner by adjusting the trigger point
      const scrollProgress = Math.max(0, Math.min(1,
        1.3 - (containerRect.top / windowHeight)
      ));

      // Move text up by 80% of its height based on scroll progress
      const translateY = -80 * scrollProgress;
      const opacity = scrollProgress;

      textBannerRef.current.style.transform = `translateY(${translateY}%)`;
      textBannerRef.current.style.opacity = `${opacity}`;
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <SidebarProvider>
      <div className="min-h-screen w-full flex flex-col bg-background overflow-x-clip">
        <Navbar enableScrollHide />
        <main className="flex-grow">
          <RedesignedHero />
        </main>
        <div ref={containerRef} className="relative">
          {/* Large TAG-Innovation text banner - rises from behind footer */}
          <div
            ref={textBannerRef}
            className="absolute top-0 left-0 w-full text-primary text-center pointer-events-none flex items-center justify-center"
            style={{ opacity: 0, transform: 'translateY(0)' }}
          >
            <h1 className="text-[12.5vw] leading-none whitespace-nowrap" style={{ fontWeight: 600, textShadow: '0 2px 4px hsl(var(--primary) / 0.3)' }}>TAG-Innovation</h1>
          </div>
          <div className="relative z-10 bg-background">
            <Footer />
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Index;
